import React, { useEffect, useMemo, useState } from "react";
import { phappsRequest } from "@/services/apiService";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { BarChart, Memory, Speed } from "@mui/icons-material";
import ChartCard from "@/components/data-visuals/ChartCard";
import AdminCherubStatCard from "@/components/admin/AdminCherubStatCard";
import SFDateRangeSelector from "@/components/inputs/SFDateRangeSelector";
import { API_PATHS, CHERUB_ADMIN_SSE_PATH } from "@/config/apiConstants";
import { CHERUB_COLORS } from "@/config/colors";
import {
  adminDateRangePickerWideNestedSx,
  cherubDataGridSx,
} from "@/utils/adminTableStyles";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";

const PH_BLUE   = "#002169";
const PH_YELLOW = "#FFAF3C";

const fmtTokens = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const num = Number(n);
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000)     return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString();
};

const fmtMs = (ms) => (ms != null ? `${ms.toLocaleString()} ms` : "—");

const getDefaultDates = (days = 30) => {
  const today = new Date();
  const start = new Date();
  start.setDate(today.getDate() - days);
  return {
    dateStart: start.toISOString().split("T")[0],
    dateEnd:   today.toISOString().split("T")[0],
  };
};

const DateRangeBar = ({ dateStart, dateEnd, onChange }) => (
  <Box
    sx={{
      borderRadius: "8px", p: 2, boxSizing: "border-box", width: "100%", minWidth: 0,
      backgroundColor: CHERUB_COLORS.SKY_DARK, display: "flex", alignItems: "center",
      overflow: "hidden", height: 72, minHeight: 72, maxHeight: 72,
      "& .text-black": { color: "white !important" },
      ...adminDateRangePickerWideNestedSx,
    }}
  >
    <SFDateRangeSelector
      adminToolbar
      value={[new Date(dateStart), new Date(dateEnd)]}
      onChange={(range) => onChange(
        range[0]?.toISOString().split("T")[0],
        range[1]?.toISOString().split("T")[0],
      )}
    />
  </Box>
);

const gapsColumns = [
  {
    field: "UserQuestion", headerName: "Question", flex: 1, minWidth: 250, headerAlign: "left", align: "left",
    renderCell: (p) => (
      <Tooltip title={p.value} placement="top-start">
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
          {p.value?.length > 80 ? p.value.slice(0, 80) + "…" : p.value}
        </span>
      </Tooltip>
    ),
  },
  { field: "AppSection",      headerName: "Section",    width: 120, headerAlign: "center", align: "center", renderCell: (p) => p.value || "—" },
  { field: "TimesAsked",      headerName: "Asked",      width: 80,  type: "number", headerAlign: "center", align: "center" },
  { field: "PharmaciesAsked", headerName: "Pharmacies", width: 100, type: "number", headerAlign: "center", align: "center" },
  {
    field: "LastAsked", headerName: "Last asked", width: 140, type: "dateTime", headerAlign: "center", align: "center",
    valueGetter: (_v, row) => (row.LastAsked ? new Date(row.LastAsked) : null),
    renderCell: (p) => p.row.LastAsked
      ? new Date(p.row.LastAsked).toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" })
      : "—",
  },
];

const AdminCherubPage = () => {
  const defaults = getDefaultDates(30);
  const [dateStart, setDateStart] = useState(defaults.dateStart);
  const [dateEnd, setDateEnd]     = useState(defaults.dateEnd);

  const [overviewData, setOverviewData]     = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const [gapsData, setGapsData]     = useState([]);
  const [gapsLoading, setGapsLoading] = useState(true);

  const [fetchError, setFetchError]       = useState(null);
  const [sectionFilter, setSectionFilter] = useState("");
  const [searchFilter, setSearchFilter]   = useState("");
  const [tokenChartOpen, setTokenChartOpen] = useState(false);

  // Fetch both overview + gaps whenever date range changes
  useEffect(() => {
    if (!dateStart || !dateEnd) return;
    let cancelled = false;
    setOverviewLoading(true);
    setGapsLoading(true);
    Promise.all([
      phappsRequest(API_PATHS.ADMIN_FUNCTIONS_CHERUB,      "GET", null, { DateStart: dateStart, DateEnd: dateEnd }),
      phappsRequest(API_PATHS.ADMIN_FUNCTIONS_CHERUB_GAPS, "GET", null, { DateStart: dateStart, DateEnd: dateEnd }),
    ])
      .then(([overview, gaps]) => {
        if (cancelled) return;
        setOverviewData(overview);
        setGapsData(gaps?.gaps || []);
        setFetchError(null);
        setOverviewLoading(false);
        setGapsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setFetchError(getAdminApiErrorMessage(err, "Failed to load data"));
        setOverviewLoading(false);
        setGapsLoading(false);
      });
    return () => { cancelled = true; };
  }, [dateStart, dateEnd]);

  // SSE live updates
  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (!token) return;
    const url = `${import.meta.env.VITE_APP_DATA_API_URL}${CHERUB_ADMIN_SSE_PATH}?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);

    es.addEventListener("session_created", (e) => {
      try {
        const d = JSON.parse(e.data);
        const startedAt = new Date(d.startedAt);
        if (startedAt >= new Date(dateStart) && startedAt <= new Date(`${dateEnd}T23:59:59`)) {
          setOverviewData((prev) => prev ? {
            ...prev,
            Summary: { ...prev.Summary, TotalSessions: (prev.Summary?.TotalSessions || 0) + 1 },
          } : prev);
        }
      } catch {}
    });

    es.addEventListener("session_ended", (e) => {
      try {
        setOverviewData((prev) => prev ? {
          ...prev,
          Summary: { ...prev.Summary, CompletedSessions: (prev.Summary?.CompletedSessions || 0) + 1 },
        } : prev);
      } catch {}
    });

    es.addEventListener("gap_logged", (e) => {
      try {
        const d = JSON.parse(e.data);
        setGapsData((prev) => {
          const idx = prev.findIndex((g) => g.UserQuestion === d.userQuestion && g.AppSection === d.appSection);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = { ...next[idx], TimesAsked: next[idx].TimesAsked + 1, LastAsked: d.createdAt };
            return next;
          }
          return [{ UserQuestion: d.userQuestion, AppSection: d.appSection, TimesAsked: 1, PharmaciesAsked: 1, LastAsked: d.createdAt }, ...prev];
        });
      } catch {}
    });

    return () => es.close();
  }, [dateStart, dateEnd]);

  // Derived stats
  const summary            = overviewData?.Summary || {};
  const tokensPerDay       = overviewData?.TokensPerDay || [];
  const responseTimePerDay = overviewData?.ResponseTimePerDay || [];

  const totalTokens = (summary.TotalInputTokens || 0) + (summary.TotalCacheCreationTokens || 0) +
    (summary.TotalCacheReadTokens || 0) + (summary.TotalOutputTokens || 0);

  const avgResponseMs = responseTimePerDay.length > 0
    ? Math.round(responseTimePerDay.reduce((s, r) => s + Number(r.AvgResponseMs), 0) / responseTimePerDay.length)
    : null;

  const todayStr    = new Date().toISOString().split("T")[0];
  const todayRow    = responseTimePerDay.find((r) => String(r.Day).slice(0, 10) === todayStr);
  const todayRespMs = todayRow ? Number(todayRow.AvgResponseMs) : null;

  const tokenPieData = useMemo(() => {
    const inp = Number(summary.TotalInputTokens || 0);
    const cc  = Number(summary.TotalCacheCreationTokens || 0);
    const cr  = Number(summary.TotalCacheReadTokens || 0);
    const out = Number(summary.TotalOutputTokens || 0);
    const items = [
      { x: "Regular Input", y: Math.max(0, inp), color: CHERUB_COLORS.SKY_DARK },
      { x: "Cache Create",  y: cc,               color: CHERUB_COLORS.SKY_MID  },
      { x: "Cache Read",    y: cr,               color: CHERUB_COLORS.TEAL     },
      { x: "Output",        y: out,              color: CHERUB_COLORS.AMBER    },
    ].filter((d) => d.y > 0);
    return items.length ? items : [{ x: "No data", y: 1, color: CHERUB_COLORS.SKY_LIGHT }];
  }, [summary]);

  const sections = useMemo(
    () => [...new Set(gapsData.map((r) => r.AppSection).filter(Boolean))].sort(),
    [gapsData],
  );

  const filteredRows = useMemo(() => {
    let rows = gapsData;
    if (sectionFilter) rows = rows.filter((r) => r.AppSection === sectionFilter);
    if (searchFilter)  rows = rows.filter((r) => r.UserQuestion?.toLowerCase().includes(searchFilter.toLowerCase()));
    return rows.map((r, i) => ({ ...r, id: i }));
  }, [gapsData, sectionFilter, searchFilter]);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <div className="p-4 space-y-4 w-full max-w-full">
        <Typography variant="h5" fontWeight="bold" sx={{ color: CHERUB_COLORS.SKY_DARK }}>
          Cherub AI
        </Typography>

        {fetchError && <Alert severity="warning">{fetchError}</Alert>}

        <DateRangeBar dateStart={dateStart} dateEnd={dateEnd}
          onChange={(s, e) => { setDateStart(s); setDateEnd(e); }} />

        {/* Stat cards */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="min-w-0">
            <AdminCherubStatCard title="Total tokens" value={totalTokens} valueFormatter={fmtTokens}
              Icon={Memory} caption={`$${Number(summary.EstimatedCostUSD || 0).toFixed(4)} USD est.`} />
          </div>
          <div className="min-w-0">
            <AdminCherubStatCard title="Avg response time" value={fmtMs(avgResponseMs)}
              valueFormatter={(v) => v} Icon={Speed} textValue caption="period average" />
          </div>
          <div className="min-w-0">
            <AdminCherubStatCard title="Response time today" value={fmtMs(todayRespMs)}
              valueFormatter={(v) => v} Icon={Speed} textValue />
          </div>
        </section>

        {/* Main content: gaps grid (2/3) + token pie (1/3) */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
          <div className="xl:col-span-2 min-w-0">
            <Card variant="outlined" sx={{ borderColor: PH_YELLOW, borderRadius: 2, overflow: "hidden" }}>
              <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                <Box sx={{
                  px: 2, py: 1.5, backgroundColor: "#FFF8E7", borderBottom: "2px solid #FFE8A3",
                  display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
                }}>
                  <Typography fontWeight={700} sx={{ color: PH_BLUE, flex: "0 0 auto" }}>
                    Knowledge gaps
                  </Typography>
                  <Select size="small" value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)}
                    displayEmpty sx={{ minWidth: 160 }}>
                    <MenuItem value="">All sections</MenuItem>
                    {sections.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                  <TextField size="small" placeholder="Search questions…" value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)} sx={{ minWidth: 220 }} />
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    {filteredRows.length} question{filteredRows.length !== 1 ? "s" : ""}
                  </Typography>
                </Box>
                <Box sx={{ height: "calc(100vh - 460px)", minHeight: 360 }}>
                  <DataGrid
                    rows={filteredRows} columns={gapsColumns} loading={gapsLoading}
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    disableRowSelectionOnClick
                    sx={{ ...cherubDataGridSx, height: "100%", border: "none" }}
                  />
                </Box>
              </CardContent>
            </Card>
          </div>

          <div className="min-w-0 flex flex-col gap-3">
            <ChartCard
              chartId="cherub-token-pie" title="Token breakdown" hideMainChart
              data={{}} primaryXAxis={{}} primaryYAxis={{}} borderClass="border-4 border-sky-200"
              series={[]} pieData={tokenPieData} pieTitle="By token type"
            />
            <Button variant="outlined" size="small" startIcon={<BarChart />}
              onClick={() => setTokenChartOpen(true)}
              sx={{
                textTransform: "none", fontWeight: 600,
                borderColor: CHERUB_COLORS.SKY_MID, color: CHERUB_COLORS.SKY_DARK,
                "&:hover": { borderColor: CHERUB_COLORS.SKY_DARK, bgcolor: CHERUB_COLORS.SKY_TINT },
                alignSelf: "flex-start",
              }}>
              Token usage over time
            </Button>
          </div>
        </div>

        {/* Token usage over time dialog */}
        <Dialog open={tokenChartOpen} onClose={() => setTokenChartOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ color: CHERUB_COLORS.SKY_DARK, fontWeight: 700 }}>Token usage over time</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <ChartCard title="" hidePieChart mainChartMinHeight={360}
                data={{ tokensPerDay }}
                primaryXAxis={{ valueType: "DateTime", title: "Date", labelFormat: "dd/MM", intervalType: "Days" }}
                primaryYAxis={{ title: "Tokens", labelFormat: "{value}" }}
                borderClass="border-4 border-sky-200"
                series={[
                  { dataSource: tokensPerDay, xName: "Date", yName: "InputTokens",         type: "StackingColumn", name: "Regular Input", fill: CHERUB_COLORS.SKY_DARK, columnSpacing: 0.4 },
                  { dataSource: tokensPerDay, xName: "Date", yName: "CacheCreationTokens", type: "StackingColumn", name: "Cache Create",  fill: CHERUB_COLORS.SKY_MID,  columnSpacing: 0.4 },
                  { dataSource: tokensPerDay, xName: "Date", yName: "CacheReadTokens",     type: "StackingColumn", name: "Cache Read",    fill: CHERUB_COLORS.TEAL,      columnSpacing: 0.4 },
                  { dataSource: tokensPerDay, xName: "Date", yName: "OutputTokens",        type: "StackingColumn", name: "Output",        fill: CHERUB_COLORS.AMBER,     columnSpacing: 0.4 },
                ]}
                pieData={[]}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTokenChartOpen(false)} color="primary">Close</Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminCherubPage;
